import {
  getFormConfiguration,
  validateField,
  validateGridCell,
  validateForm,
  validateGrid,
  shouldAutoSave,
  updateFieldError as addFieldError,
  updateGridFieldError as addGridFieldError,
  updateGridFieldVis,
  updateFieldVis,
  FormDataWrapper,
  clearTableRowError
} from "../../form";
import { PRODUCTS } from "../../constants/account.constants";
import { DataService, PublicService } from "../services";
import { isEmpty, cloneInstanceObject } from "../utils/utils";
import { createStoreWithImmer } from "./base";
import { useSectionStore } from "./section.store";

const STORE = {
  formData: null,
  flowData: {},
  schema: {},
  modelConfig: {},
  error: {},
  visiblity: {},
  shownOnScreen: {},
  pendingSignature: [],
  geoFieldsList: {},
  systemFields: [],
  gridRowValidationError: {},
  currentUser: null,
  isSplitView: false
};

export function createFormStore(uId) {
  const useFormStore = createStoreWithImmer(uId, stateFunction);
  return useFormStore;
}

const stateFunction = (setState, getState) => ({
  ...STORE,
  init: (formData, schema, flowData) => {
    let systemFields = getState().systemFields;
    let modelConfig = getFormConfiguration(schema, flowData, systemFields);
    setState(() => {
      return {
        formData: formData, //why is it need to be updated here
        schema: schema,
        flowData: flowData,
        modelConfig: modelConfig
      };
    });
  },
  updateModelConfig: (modelId, columnId, updatedColumnData = {}) => {
    setState((state) => {
      if (!state.modelConfig[modelId]) {
        state.modelConfig[modelId] = {};
      }
      if (!state.modelConfig[modelId][columnId]) {
        state.modelConfig[modelId][columnId] = {};
      } else if (updatedColumnData) {
        state.modelConfig[modelId][columnId] = {
          ...state.modelConfig[modelId][columnId],
          ...updatedColumnData
        };
      }
    });
  },
  updateField: (value, field, saveChanges) => {
    let formData = getState().formData;
    formData && formData.update(field.Id, value);
    // setState(() => {
    //   return {
    //     formData: FormDataWrapper(formData.dataSet, formData.changeSet)
    //   };
    // });
    if (saveChanges && shouldAutoSave(field, saveChanges)) {
      getState().saveChanges(formData);
    }
    return getState().validateFormField(field, formData);
  },
  addTableRow: (tableId, count = 1) => {
    let formData = getState().formData;
    for (let i = 0; i < count; i++) {
      tableId && formData.create(tableId);
    }
    return getState().saveChanges(formData);
  },
  deleteTableRow: (tableId, rowIds) => {
    let formData = getState().formData;
    rowIds.forEach((id) => {
      formData.delete(tableId, id);
    });
    getState().onGridRowDelete(tableId, rowIds);
    getState().saveChanges(formData);
  },
  onGridRowDelete: (tableId, rowIds) => {
    let errorObj = getState().error;
    errorObj = clearTableRowError(tableId, rowIds, errorObj);
    setState(() => {
      return { error: errorObj };
    });
  },
  updateTableField: (tableId, rowId, field, value, saveChanges) => {
    let formData = getState().formData;
    let visiblity = getState().visiblity;
    let tableRows = formData.getList(tableId);
    let tableRow = tableRows.find((row) => row.getValue("_id") === rowId);
    if (tableRow) {
      tableRow.update(field.id || field.Id, value);
    }
    setState(() => {
      return {
        formData: FormDataWrapper(formData.dataSet, formData.changeSet)
      };
    });

    let errorObj = validateGridCell(
      field.modelConfig,
      tableRow,
      tableId,
      visiblity,
      formData
    );
    errorObj &&
      getState().updateGridFieldError(
        errorObj.modelId,
        errorObj.rowId,
        errorObj.fieldId,
        errorObj.error
      );
    if (saveChanges && shouldAutoSave(field, saveChanges)) {
      return getState().saveChanges(formData);
    }
  },
  saveChanges: (formData) => {
    let changeSummary = formData?.getChangeSummary();
    let flowData = { ...getState().flowData };
    flowData.currentUser = getState().currentUser;
    return new Promise((resolve, reject) => {
      if (flowData.isAdmin) {
        let [promise] = saveData(changeSummary, flowData);
        promise().then((changeSummary) => {
          getState().mergeChanges(formData, changeSummary);
          resolve();
        });
      } else {
        saveData(changeSummary, flowData, (err, data, onDone) => {
          getState().mergeChanges(formData, data);
          let updatedChangeSummary = getState().getChangeSummary();
          updatedChangeSummary &&
            Reflect.deleteProperty(updatedChangeSummary, "Table::");
          onDone(updatedChangeSummary);
          resolve(data);
        });
      }
    });
  },
  saveTableChanges: (formData, changeSummary, tableId) => {
    formData = getState().formData;
    let flowData = { ...getState().flowData };
    flowData.currentUser = getState().currentUser;
    return new Promise((resolve, reject) => {
      if (formData) {
        formData.merge(changeSummary);
        setState(() => {
          let formDataUpdated = FormDataWrapper(
            formData.dataSet,
            formData.changeSet
          );
          return { formData: formDataUpdated };
        });
      }
      if (flowData.isAdmin) {
        let [promise] = saveData(changeSummary, flowData);
        promise().then((changeSummary) => {
          getState().mergeChanges(formData, changeSummary);
          resolve();
        });
      } else {
        saveData(changeSummary, flowData, (err, data, onDone) => {
          getState().mergeChanges(formData, data);
          let updatedChangeSummary = getState().getChangeSummary();
          updatedChangeSummary &&
            Reflect.deleteProperty(updatedChangeSummary, "Table::");
          getState().validateGridCells(
            formData,
            tableId,
            changeSummary[`Table::${tableId}`]
          );
          onDone(updatedChangeSummary);
          resolve(data);
        });
      }
    });
  },
  // This below function to validate the grid on bulk operations
  validateGridCells: (formData, tableId, changeSummary) => {
    let modelConfig = getState().modelConfig;
    let visiblity = getState().visiblity;
    let formErrors = getState().error;
    let error = validateGrid(
      formData,
      tableId,
      modelConfig,
      formErrors,
      visiblity,
      changeSummary
    );
    setState(() => {
      return { error: error };
    });
  },
  mergeChanges: (formData, changeSummary) => {
    // if (!formData) {
    //   formData = getState().formData;
    // }
    formData = getState().formData;
    if (!changeSummary) {
      changeSummary = formData?.getChangeSummary();
    }
    if (formData) {
      formData.merge(changeSummary);
      setState(() => {
        let formDataUpdated = FormDataWrapper(
          formData.dataSet,
          formData.changeSet
        );
        return { formData: formDataUpdated };
      });
    }
  },
  getChangeSummary: () => {
    let formData = getState().formData;
    return formData?.getChangeSummary();
  },
  validateFormField: (field, formData) => {
    let schema = getState().schema;
    if (isEmpty(schema)) return;
    let modelConfig = getState().modelConfig;
    let modelId = schema.getModel().getValue("Id");
    let visiblity = getState().visiblity;
    let parentModelCofiguration = modelConfig[modelId];
    let column = schema.getObject(field.Column);
    if (column) {
      let errorObj = validateField(
        column.getValue("Id"),
        formData,
        parentModelCofiguration,
        visiblity
      );
      errorObj && getState().updateFieldError(errorObj.fieldId, errorObj.error);
    }
  },
  updateGridFieldError: (tableId, rowId, fieldId, error) => {
    let errorObj = getState().error;
    errorObj = addGridFieldError(tableId, rowId, fieldId, error, errorObj);
    setState(() => {
      return { error: errorObj };
    });
  },
  updateFieldError: (fieldId, error) => {
    let errorObj = getState().error;
    errorObj = addFieldError(fieldId, error, errorObj);
    setState(() => {
      return { error: errorObj };
    });
  },
  onFieldRemoved: (fieldId, columnId, tableId, rowId) => {
    getState().updateFieldError(fieldId);
    // let flowId = getState().flowData.flowId;
    // flowId &&
    //   getState().updateModelConfig(flowId, columnId, {
    //     renderedOnUI: false
    //   });
    let visiblity = getState().visiblity;
    let isVisiblie = false;
    if (tableId && rowId) {
      visiblity = updateGridFieldVis(
        tableId,
        rowId,
        columnId,
        isVisiblie,
        visiblity
      );
    } else {
      visiblity = updateFieldVis(columnId, isVisiblie, visiblity);
    }
    setState(() => {
      return { visiblity: visiblity };
    });
  },
  onFieldHiddenOnScreen: (fieldId, columnId) => {
    let shownOnScreen = getState().shownOnScreen;
    let isVisiblie = false;
    shownOnScreen = updateFieldVis(columnId, isVisiblie, shownOnScreen);
    setState(() => {
      return { shownOnScreen: shownOnScreen };
    });
  },
  updateGeoList: (fieldId, value) => {
    if (fieldId && value) {
      let geoList = { ...getState().geoFieldsList };
      if (!geoList[fieldId]) {
        geoList[fieldId] = {};
      }
      geoList[fieldId] = value;
      setState(() => {
        return { geoFieldsList: geoList };
      });
    }
  },
  onFieldAdded: (fieldId, columnId, tableId, rowId) => {
    if (fieldId && columnId) {
      // let flowData = getState().flowData;
      // flowData.flowId &&
      //   getState().updateModelConfig(flowData.flowId, columnId, {
      //     renderedOnUI: true
      //   });
      let visiblity = getState().visiblity;
      let isVisiblie = true;
      if (tableId && rowId) {
        visiblity = updateGridFieldVis(
          tableId,
          rowId,
          columnId,
          isVisiblie,
          visiblity
        );
      } else {
        visiblity = updateFieldVis(columnId, isVisiblie, visiblity);
      }
      setState(() => {
        return { visiblity: visiblity };
      });
    }
  },
  onFieldShownOnScreen: (fieldId, columnId) => {
    let shownOnScreen = getState().shownOnScreen;
    let isVisiblie = true;
    shownOnScreen = updateFieldVis(columnId, isVisiblie, shownOnScreen);
    setState(() => {
      return { shownOnScreen: shownOnScreen };
    });
  },
  onTableAdded: (tableId, data, visibleOnScreen) => {
    if (tableId && data) {
      setState((state) => {
        if (!state.visiblity[tableId]) {
          state.visiblity[tableId] = {};
        }
        state.visiblity[tableId] = data;
        if (!state.shownOnScreen[tableId]) {
          state.shownOnScreen[tableId] = {};
        }
        state.shownOnScreen[tableId] = visibleOnScreen;
      });
    }
  },
  updatePendingValues: async (formData, geoValuesList) => {
    if (Object.keys(geoValuesList).length > 0) {
      for (let fieldId in geoValuesList) {
        formData && formData.update(fieldId, geoValuesList[fieldId]);
      }
      setState(() => {
        return { geoFieldsList: {} };
      });
      await getState().saveChanges(formData);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 500);
      });
    }
  },
  validateFullForm: async () => {
    let formData = getState().formData;
    let isValid = true;
    let geoValuesList = getState().geoFieldsList;
    await getState().updatePendingValues(formData, geoValuesList);
    await expandAllRows(formData);
    let modelConfig = getState().modelConfig;
    let schema = getState().schema;
    let rootId = getState().flowData.flowId;
    let visiblity = getState().visiblity;
    let gridRowValidationError = getState().gridRowValidationError;
    let error = validateForm(formData, modelConfig, rootId, visiblity);
    // let error = getState().error;
    setState(() => {
      return { error: error };
    });

    for (let modelName in error) {
      if (modelName === "_root") {
        let fieldError = Object.keys(error["_root"]) || [];
        if (!fieldError.isEmpty()) {
          isValid = false;
        }
      } else {
        for (let row in error[modelName]) {
          // Here we ignoring the hidden table's errors
          let currentModel = schema.getObject(modelName);
          let column = schema.getObject(currentModel.getValue("Column"));
          let columnId = column.getValue("Id");
          let { isHidden } = modelConfig[rootId][columnId];
          if (!isHidden) {
            let fieldError = Object.keys(error[modelName][row]) || [];
            if (!fieldError.isEmpty()) {
              isValid = false;
            }
          }
        }
      }
    }

    if (Object.keys(gridRowValidationError).length > 0) {
      isValid = false;
    }
    return isValid;
  },
  setSignatureStatus: (fieldId, status) => {
    let pendingSignature = getState().pendingSignature;
    pendingSignature = [...pendingSignature];
    if (status === "Done") {
      pendingSignature.pop(fieldId);
    } else {
      pendingSignature.push(fieldId);
    }
    setState(() => {
      return { pendingSignature: pendingSignature };
    });
  },
  setGridRowValidationError: (modelId, error) => {
    let gridRowValidationError = getState().gridRowValidationError;
    gridRowValidationError = { ...gridRowValidationError };
    if (error) {
      gridRowValidationError[modelId] = error;
    } else {
      Reflect.deleteProperty(gridRowValidationError, modelId);
    }
    setState(() => {
      return { gridRowValidationError: gridRowValidationError };
    });
  },
  reset: () => {
    setState(() => ({
      ...STORE
    }));
  },
  toJSON: () => {
    let formData = getState().formData;
    let newFormData = cloneInstanceObject(formData);
    const changes = formData.getChangeSummary();
    newFormData.merge(changes);
    return newFormData;
  }
});

function saveData(params, flowData, callback) {
  // const flowData = getState().flowData;
  Reflect.deleteProperty(params, "Table::");
  if (flowData.isPublicForm) {
    return PublicService.postq(
      flowData.saveUrl || flowData.baseUrl,
      {
        data: params,
        options: {
          recaptcha_action: "savePublicForm"
        },
        toast: flowData.flowType !== PRODUCTS.CASE
      },
      callback
    );
  } else if (flowData.isAdmin) {
    return DataService.update(flowData.saveUrl || flowData.adminBaseUrl, {
      data: params,
      toast: flowData.flowType !== PRODUCTS.CASE
    });
  } else {
    let header = {};
    if (flowData.mode === "Preview" && flowData.currentUser) {
      header = {
        "X-Preview-Impersonated-User-Id": flowData.currentUser["_id"]
      };
    }
    return DataService.postq(
      flowData.saveUrl || flowData.baseUrl,
      {
        data: params,
        header: header,
        toast: flowData.flowType !== PRODUCTS.CASE
      },
      callback
    );
  }
}

function expandAllRows(formData) {
  let sections = useSectionStore.getState().sections;
  let isPWA = false;
  sections.forEach((section) => {
    if (section.accrodianGroup) {
      isPWA = true;
      let rows = formData.getList(section.accrodianGroup.id);
      rows = rows.map((row) => {
        return row.id;
      });
      if (section.accrodianGroup.accrodianGroupRef) {
        section.accrodianGroup.accrodianGroupRef.expandAll(rows);
      }
    }
  });

  return new Promise((resolve) => {
    isPWA
      ? setTimeout(() => {
          resolve();
        }, 400)
      : resolve();
  });
}
